import { KeyValuePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AutocompletePersonComponent } from 'src/app/shared/components/autocomplete-person/autocomplete-person.component';
import { EditorComponent } from 'src/app/shared/components/editor/editor.component';
import { IfHasPermissionDirective } from 'src/app/shared/directives/if-has-permission.directive';
import { AdminProjectDetail, IEmployeeSimple } from 'src/app/shared/models';
import { AlertService, EmployeeService, ProjectService } from 'src/app/shared/services';
import { errorMessage } from 'src/app/shared/utils';
import { BillingRateEnum } from '../../../shared/enums/billing-rate.enum';
import { BillingTypeEnum } from '../../../shared/enums/billing-type.enum';

@Component({
  selector: 'app-edit-projects',
  templateUrl: './edit-project.component.html',
  styleUrls: ['./edit-project.component.scss'],
  imports: [
    KeyValuePipe,
    ReactiveFormsModule,
    RouterLink,
    TranslateModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    AutocompletePersonComponent,
    EditorComponent,
    IfHasPermissionDirective,
  ],
})
export class EditProjectComponent implements OnInit {
  private readonly alertService = inject(AlertService);
  private readonly employeeService = inject(EmployeeService);
  private readonly projectService = inject(ProjectService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  translate = inject(TranslateService);

  project: AdminProjectDetail;
  employees: IEmployeeSimple[] = [];
  readonly billingRates = BillingRateEnum;
  readonly billingTypes = BillingTypeEnum;

  loading = true;

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl<string | null>(null),
    manager: new FormControl<IEmployeeSimple | null>(null, [Validators.required]),
    lead: new FormControl<IEmployeeSimple | null>(null),
    engineeringManager: new FormControl<IEmployeeSimple | null>(null, [Validators.required]),
    oracleExternalId: new FormControl<string | null>(null),
    isTimelogRequired: new FormControl<boolean | null>(false),
    billingProperties: new FormGroup({
      billingRate: new FormControl<BillingRateEnum>(BillingRateEnum.PER_DAY),
      billingType: new FormControl<BillingTypeEnum>(BillingTypeEnum.UNDEFINED),
    }),
  });

  ngOnInit(): void {
    this.employeeService.getAllEmployeesSimple().subscribe((result) => {
      this.employees = result;
    });

    this.projectService.loadProjectById(this.route.snapshot.params.id).subscribe((result) => {
      this.project = result;
      this.form.setValue({
        name: result.name,
        description: result.description,
        manager: result.manager,
        lead: result.lead,
        engineeringManager: result.engineeringManager,
        oracleExternalId: result.oracleExternalId,
        isTimelogRequired: result.isTimelogRequired || false,
        billingProperties: {
          billingRate: result.billingProperties.billingRate,
          billingType: result.billingProperties.billingType,
        },
      });
      this.loading = false;
    });
  }

  handleSubmit() {
    if (this.form.invalid) return this.form.markAllAsTouched();

    this.projectService
      .updateProject({
        id: this.project.id,
        name: this.form.value.name,
        description: this.form.value.description,
        manager: this.form.value.manager.id,
        lead: this.form.value.lead?.id || null,
        engineeringManager: this.form.value.engineeringManager.id,
        oracleExternalId: this.form.value.oracleExternalId,
        isTimelogRequired: this.form.value.isTimelogRequired,
        billingProperties: {
          billingRate: this.form.value.billingProperties.billingRate,
          billingType: this.form.value.billingProperties.billingType,
        },
      })
      .subscribe({
        next: () => {
          this.alertService.showSuccess('ADMIN.LICENSES.SUCCESSFULLY_UPDATED');
          this.router.navigate(['../../'], { relativeTo: this.route });
        },
        error: (err) => this.alertService.showError(errorMessage(err)),
      });
  }
}
